import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {Box, ButtonBase, InputBase, MenuItem, Select, Skeleton, Stack, Typography} from '@mui/material';
import {useAtom, useAtomValue} from 'jotai';
import {useNavigate} from 'react-router-dom';
import {HugeiconsIcon} from '@hugeicons/react';
import {Add01Icon, DatabaseIcon, Search01Icon} from '@hugeicons/core-free-icons';
import {SubProjectOption} from '../../SubProjectSelect';
import {KnowledgeManagerRepository} from '../../../../repositories/KnowledgeManagerRepository';
import {useStompWebsocketKbManagerMulti} from '../../../../hooks/useStompWebsocketKbManagerMulti';
import {
  topicsAtom, selectedTopicAtom, topicSearchAtom, topicLanguageFilterAtom, topicsLoadingAtom, projectAtom,
} from '../atoms';

const CARD_SHADOW = "0 2px 12px rgba(35, 38, 77, 0.07)";
const SPRING = "cubic-bezier(0.34, 1.56, 0.64, 1)";

const controlSx = {
  height: 40, display: "flex", alignItems: "center", gap: 1, px: 1.75, borderRadius: "12px", bgcolor: "#fff",
  boxShadow: CARD_SHADOW, color: "#8A8AA0", fontSize: 13,
};

export const hexToRgba = (hex, alpha) => {
  const clean = (hex || "#8491F7").replace("#", "");
  const full = clean.length === 3 ? clean.split("").map(c => c + c).join("") : clean;
  const r = parseInt(full.substring(0, 2), 16);
  const g = parseInt(full.substring(2, 4), 16);
  const b = parseInt(full.substring(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

// Index status of a topic's documents: a small coloured dot and one quiet line of text
const IndexStatus = ({statuses}) => {
  if (!statuses) return <Skeleton width={120} height={16} sx={{borderRadius: '6px'}}/>;
  const total = Object.keys(statuses).length;
  if (total === 0) return <Typography sx={{fontSize: 12, color: '#8A8AA0'}}>No documents yet</Typography>;
  const values = Object.values(statuses);
  const indexed = values.filter(status => status === 'indexed').length;
  const failed = values.filter(status => status === 'failed').length;
  const tone = failed > 0
    ? {label: `${failed} failed · ${indexed}/${total} indexed`, dot: '#F2A0A0'}
    : indexed === total
      ? {label: 'Fully indexed', dot: '#9ADBB8'}
      : {label: `${indexed}/${total} indexed`, dot: '#F2CF8E'};

  return (
    <Stack direction="row" alignItems="center" gap={0.9}>
      <Box component="span" sx={{width: 8, height: 8, borderRadius: '50%', flexShrink: 0, bgcolor: tone.dot}}/>
      <Typography noWrap sx={{fontSize: 12.5, color: '#6B6B80'}}>{tone.label}</Typography>
    </Stack>
  );
};

const TopicCard = ({topic, statuses, languageLabel, onOpen}) => (
  <ButtonBase
    onClick={() => onOpen(topic)}
    sx={{
      display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: 1.75, textAlign: 'left', p: 2.25, borderRadius: '18px',
      bgcolor: '#fff', boxShadow: CARD_SHADOW, minWidth: 0,
      transition: `transform 0.45s ${SPRING}, box-shadow 0.25s ease`,
      '&:hover': {transform: 'translateY(-4px)', boxShadow: '0 10px 28px rgba(35, 38, 77, 0.12)'},
    }}
  >
    <Stack direction="row" alignItems="center" gap={1.5}>
      <Box sx={{
        width: 44, height: 44, borderRadius: '14px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: topic.color || '#8491F7', bgcolor: hexToRgba(topic.color, 0.14),
      }}>
        <HugeiconsIcon icon={DatabaseIcon} size={21}/>
      </Box>
      <Box sx={{minWidth: 0, flex: 1}}>
        <Typography noWrap sx={{fontSize: 16, fontWeight: 700, color: '#23264D', letterSpacing: '-0.01em'}}>{topic.name}</Typography>
        {languageLabel && (
          <Typography sx={{fontSize: 11, fontWeight: 700, color: '#6B6B80', textTransform: 'uppercase'}}>{languageLabel}</Typography>
        )}
      </Box>
    </Stack>

    <Typography sx={{
      fontSize: 13, color: '#6B6B80', minHeight: 38, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
    }}>
      {topic.description || 'No description'}
    </Typography>

    {topic.tags?.length > 0 && (
      <Stack direction="row" gap={0.75} flexWrap="wrap">
        {topic.tags.slice(0, 3).map(tag => (
          <Box key={tag} component="span" sx={{px: 1.1, py: 0.25, borderRadius: '8px', fontSize: 12, color: '#6B6B80', bgcolor: '#FAFAFD'}}>{tag}</Box>
        ))}
        {topic.tags.length > 3 && (
          <Box component="span" sx={{px: 1.1, py: 0.25, borderRadius: '8px', fontSize: 12, color: '#6B6B80', bgcolor: '#FAFAFD'}}>+{topic.tags.length - 3}</Box>
        )}
      </Stack>
    )}

    <Stack direction="row" alignItems="flex-end" justifyContent="space-between" gap={1.5} sx={{mt: 'auto', pt: 0.5}}>
      <Box sx={{minWidth: 0, flex: '0 1 auto'}}><IndexStatus statuses={statuses}/></Box>
      <Stack direction="row" gap={1.75} sx={{fontSize: 12, color: '#8A8AA0', flexShrink: 0}}>
        <span><b style={{color: '#23264D'}}>{topic.documentCount || 0}</b> docs</span>
        <span><b style={{color: '#23264D'}}>{topic.queryCount || 0}</b> queries</span>
      </Stack>
    </Stack>
  </ButtonBase>
);

// Gallery of the project's knowledge topics; opening one shows its detail page (selectedTopicAtom)
export const TopicGallery = ({onAddClick}) => {
  const [topics] = useAtom(topicsAtom);
  const [, setSelectedTopic] = useAtom(selectedTopicAtom);
  const [search, setSearch] = useAtom(topicSearchAtom);
  const [languageFilter, setLanguageFilter] = useAtom(topicLanguageFilterAtom);
  const [loading] = useAtom(topicsLoadingAtom);
  const project = useAtomValue(projectAtom);
  const navigate = useNavigate();

  const subProjects = project?.subProjects || [];
  const activeLanguage = subProjects.find(item => String(item.id) === languageFilter);

  // Real per-document status ({topicId: {docId: status}}). The REST list is fetched only for topics whose known
  // document count differs from the topic's documentCount; status changes arrive live over the websocket.
  const [docsByTopic, setDocsByTopic] = useState({});

  useEffect(() => {
    const stale = topics.filter(t => {
      const known = docsByTopic[t.id];
      return (known ? Object.keys(known).length : -1) !== (t.documentCount || 0);
    });
    if (stale.length === 0) return;
    let cancelled = false;
    Promise.all(stale.map(t => KnowledgeManagerRepository.listDocuments(t.id)
      .then(docs => [t.id, Object.fromEntries(docs.map(d => [d.id, d.status]))])
      .catch(() => [t.id, null])))
      .then(entries => {
        if (cancelled) return;
        setDocsByTopic(prev => {
          const next = {...prev};
          entries.forEach(([id, statuses]) => {if (statuses) next[id] = statuses;});
          return next;
        });
      });
    return () => {cancelled = true;};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topics]);

  const topicDestinations = useMemo(() => topics.map(t => `/topic/topics/${t.id}/documents`), [topics]);
  const handleDocStatusPush = useCallback((update) => {
    const doc = update?.document;
    if (!doc?.topicId || !doc.id || !doc.status) return;
    setDocsByTopic(prev => ({...prev, [doc.topicId]: {...prev[doc.topicId], [doc.id]: doc.status}}));
  }, []);
  useStompWebsocketKbManagerMulti(topicDestinations, handleDocStatusPush);

  // "All sub projects" (value "") just filters in place; a specific sub project also navigates there
  const handleLanguageChange = (value) => {
    setLanguageFilter(value);
    if (!value || !project?.id) return;
    const target = subProjects.find(item => String(item.id) === value);
    if (target) navigate(`/registry-project/${project.id}/${target.id}/knowledge-manager`);
  };

  const languageName = (subProjectId) => {
    if (!subProjectId) return null;
    const match = subProjects.find(item => String(item.id) === String(subProjectId));
    return match?.languageCode || match?.languageName || null;
  };

  const filteredTopics = useMemo(() => topics.filter(topic => {
    const query = search.toLowerCase();
    const matchesSearch = topic.name?.toLowerCase().includes(query) || topic.description?.toLowerCase().includes(query)
      || (topic.tags || []).some(tag => tag.toLowerCase().includes(query));
    const matchesLanguage = !languageFilter || String(topic.subProjectId) === languageFilter;
    return matchesSearch && matchesLanguage;
  }), [topics, search, languageFilter]);

  return (
    <Stack gap={2.5}>
      <Stack direction="row" gap={1.25} alignItems="center" flexWrap="wrap">
        <Box component="label" sx={{...controlSx, width: {xs: '100%', sm: 300}, maxWidth: '100%'}}>
          <HugeiconsIcon icon={Search01Icon} size={16}/>
          <InputBase
            placeholder="Search topics"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            inputProps={{"aria-label": "Search topics"}}
            sx={{flex: 1, fontSize: 13, color: "#23264D"}}
          />
        </Box>
        {subProjects.length > 0 && (
          <Select
            variant="standard"
            disableUnderline
            displayEmpty
            value={languageFilter}
            onChange={(e) => handleLanguageChange(e.target.value)}
            renderValue={() => activeLanguage
              ? <SubProjectOption subProject={activeLanguage}/>
              : <Typography noWrap sx={{fontSize: 12, fontWeight: 600, color: "#8A8AA0"}}>All sub projects</Typography>}
            inputProps={{"aria-label": "Filter by language"}}
            sx={{
              ml: 'auto', maxWidth: 190, borderRadius: "999px", px: 1.25,
              color: activeLanguage ? "#4B55C9" : "#8A8AA0",
              bgcolor: activeLanguage ? "rgba(132, 145, 247, 0.12)" : "#fff",
              boxShadow: activeLanguage ? 'none' : CARD_SHADOW,
              "& .MuiSelect-select": {py: 0.75, pr: "22px !important", display: "flex", alignItems: "center"},
              "& .MuiSelect-icon": {color: activeLanguage ? "#4B55C9" : "#8A8AA0"},
            }}
            MenuProps={{PaperProps: {sx: {borderRadius: "12px", mt: 0.5}}}}
          >
            <MenuItem value={""} dense>
              <Typography sx={{fontSize: 12, fontWeight: 600, color: "#8A8AA0"}}>All sub projects</Typography>
            </MenuItem>
            {subProjects.map(item => (
              <MenuItem key={item.id} value={String(item.id)} dense>
                <SubProjectOption subProject={item}/>
              </MenuItem>
            ))}
          </Select>
        )}
      </Stack>

      {loading && topics.length === 0 ? (
        <Box sx={{display: 'grid', gap: 2, gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 300px), 1fr))'}}>
          {Array.from({length: 6}).map((_, index) => (
            <Skeleton key={index} variant="rounded" height={220} sx={{borderRadius: '18px'}}/>
          ))}
        </Box>
      ) : (
        <Box sx={{display: 'grid', gap: 2, gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 300px), 1fr))'}}>
          {filteredTopics.map(topic => (
            <TopicCard
              key={topic.id} topic={topic} statuses={docsByTopic[topic.id]}
              languageLabel={languageName(topic.subProjectId)} onOpen={setSelectedTopic}
            />
          ))}
          <ButtonBase
            onClick={onAddClick}
            sx={{
              display: 'flex', flexDirection: {xs: 'row', sm: 'column'}, gap: {xs: 1, sm: 1.25}, minHeight: {xs: 46, sm: 220},
              borderRadius: {xs: '12px', sm: '18px'}, color: {xs: '#4B55C9', sm: '#8A8AA0'}, fontSize: 14, fontWeight: 600,
              bgcolor: {xs: 'rgba(132, 145, 247, 0.14)', sm: 'rgba(132, 145, 247, 0.06)'},
              transition: 'background-color .2s ease, color .2s ease',
              '&:hover': {bgcolor: 'rgba(132, 145, 247, 0.12)', color: '#4B55C9'},
            }}
          >
            <Box sx={{
              width: {xs: 'auto', sm: 44}, height: {xs: 'auto', sm: 44}, borderRadius: '50%', bgcolor: {xs: 'transparent', sm: '#fff'},
              boxShadow: {xs: 'none', sm: CARD_SHADOW}, color: '#8491F7',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <HugeiconsIcon icon={Add01Icon} size={20}/>
            </Box>
            New topic
          </ButtonBase>
        </Box>
      )}

      {!loading && topics.length > 0 && filteredTopics.length === 0 && (
        <Typography sx={{fontSize: 13, color: '#8A8AA0', textAlign: 'center'}}>No topics match your search or filter</Typography>
      )}
    </Stack>
  );
};
